import React from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import { ChevronDown, Globe, MapPin, Search } from 'lucide-react';

import useLocationSearch from '../../../hooks/useLocationSearch';
import {
  FieldLabel,
  ICON_COLOR,
  INDIGO,
  autocompleteSlotSx,
  flagFor,
} from './fields';

/**
 * The location field of profile creation: one box, as a profile elsewhere on
 * the web would have it.
 *
 * A founder types a place and picks it from the list, and whichever levels
 * that place has are stored - Bengaluru fills city, state and country;
 * Karnataka fills state and country; Singapore fills only a country. Country
 * is the one level discovery requires, so it is the only one the step
 * validates; the rest is however precise the chosen place happens to be.
 *
 * The old country -> state -> city cascade is still here, behind "enter it
 * manually", for places the master tables are missing.
 */
const LocationFields = ({ formik, errorFor }) => {
  const {
    matches,
    isSearching,
    searchable,
    selectOption,
    selectedOption,
    manualEntry,
    enterManual,
    exitManual,
    countries,
    states,
    cities,
    isLoadingCountries,
    isLoadingStates,
    isLoadingCities,
    savedCountry,
    savedState,
    savedCity,
    showStateField,
    showCityField,
    onSearchInput,
    selectCountry,
    selectState,
    selectCity,
  } = useLocationSearch(formik);

  const errorText = (field) =>
    errorFor(field) ? (
      <span style={{ color: '#F04438', fontSize: '0.75rem' }}>
        {errorFor(field)}
      </span>
    ) : (
      ''
    );

  const adornment = (node) => (
    <InputAdornment position="start">{node}</InputAdornment>
  );

  // The link rides on the field's own label row. Under the field it would sit
  // between this field and the next one, reading as though it belonged to
  // whichever followed.
  const labelRow = (labelNode, link) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {labelNode}
      {link}
    </Box>
  );

  const modeLink = (label, onClick) => (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'block',
        padding: 0,
        border: 'none',
        background: 'none',
        color: INDIGO,
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.4,
        textAlign: 'left',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {label}
    </Box>
  );

  // ---------------------------------------------------------------------
  // One field: the chosen place fills whichever levels it has.
  // ---------------------------------------------------------------------
  if (!manualEntry) {
    return (
      <Grid item xs={12}>
        {labelRow(
          <FieldLabel required>LOCATION</FieldLabel>,
          modeLink("Can't find your place? Enter it manually", enterManual)
        )}
        <Autocomplete
          options={matches}
          // The server already matched and ranked the term; re-filtering here
          // would only throw away rows it chose to return.
          filterOptions={(options) => options}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, other) =>
            option?.label === other?.label
          }
          value={selectedOption}
          loading={isSearching}
          onInputChange={(event, next, reason) => {
            // Picking an option also fires this with the option's label; only
            // real typing should start a new search.
            if (reason === 'input') onSearchInput(next);
          }}
          onChange={(event, option) => selectOption(option)}
          noOptionsText={
            searchable ? 'No places found' : 'Type at least two letters'
          }
          popupIcon={null}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="filled"
              placeholder="City, state or country"
              error={!!errorFor('country_id')}
              helperText={errorText('country_id')}
              sx={autocompleteSlotSx}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: adornment(
                  <Search size={18} color={ICON_COLOR} strokeWidth={1.7} />
                ),
                endAdornment: isSearching ? (
                  <CircularProgress size={16} sx={{ marginRight: '12px' }} />
                ) : (
                  params.InputProps?.endAdornment
                ),
              }}
            />
          )}
        />
      </Grid>
    );
  }

  // ---------------------------------------------------------------------
  // Manual entry: the original cascade, for places the master tables lack.
  // Country is required; state and city are filled in only as far as the
  // founder can go.
  // ---------------------------------------------------------------------
  const span = 12 / (1 + showStateField + showCityField);

  return (
    <>
      <Grid item xs={12} sm={span}>
        {labelRow(
          <FieldLabel required>COUNTRY</FieldLabel>,
          modeLink('Search for a place instead', exitManual)
        )}
        <Autocomplete
          options={countries || []}
          loading={isLoadingCountries}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, other) => option?._id === other?._id}
          value={savedCountry || null}
          onChange={(event, option) => selectCountry(option)}
          noOptionsText="No countries"
          popupIcon={<ChevronDown size={18} color="#667085" />}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {flagFor(option) ? (
                <img
                  src={flagFor(option)}
                  alt={option?.label || 'flag'}
                  width={24}
                  height={16}
                  style={{
                    width: 24,
                    height: 16,
                    objectFit: 'cover',
                    borderRadius: 2,
                    marginRight: 8,
                  }}
                />
              ) : (
                <Globe
                  size={18}
                  color={ICON_COLOR}
                  strokeWidth={1.7}
                  style={{ marginRight: 8 }}
                />
              )}
              {option?.label}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="filled"
              placeholder="Select country"
              error={!!errorFor('country_id')}
              helperText={errorText('country_id')}
              sx={autocompleteSlotSx}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: adornment(
                  flagFor(savedCountry) ? (
                    <img
                      src={flagFor(savedCountry)}
                      alt={savedCountry?.label || 'flag'}
                      width={24}
                      height={16}
                      style={{
                        width: 24,
                        height: 16,
                        objectFit: 'cover',
                        borderRadius: 2,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Globe size={18} color={ICON_COLOR} strokeWidth={1.7} />
                  )
                ),
              }}
            />
          )}
        />
      </Grid>

      {showStateField && (
        <Grid item xs={12} sm={span}>
          <FieldLabel>STATE / PROVINCE</FieldLabel>
          <Autocomplete
            options={states || []}
            loading={isLoadingStates}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, other) => option?._id === other?._id}
            value={savedState || null}
            onChange={(event, option) => selectState(option)}
            disabled={!formik.values.country_id}
            noOptionsText="No states"
            popupIcon={<ChevronDown size={18} color="#667085" />}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                placeholder="Select state"
                sx={autocompleteSlotSx}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: adornment(
                    <MapPin size={18} color={ICON_COLOR} strokeWidth={1.7} />
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {showCityField && (
        <Grid item xs={12} sm={span}>
          <FieldLabel>CITY</FieldLabel>
          <Autocomplete
            options={cities || []}
            loading={isLoadingCities}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, other) => option?._id === other?._id}
            value={savedCity || null}
            onChange={(event, option) => selectCity(option)}
            disabled={!formik.values.state_id}
            noOptionsText="No cities"
            popupIcon={<ChevronDown size={18} color="#667085" />}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                placeholder="Select city"
                sx={autocompleteSlotSx}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: adornment(
                    <MapPin size={18} color={ICON_COLOR} strokeWidth={1.7} />
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}
    </>
  );
};

export default LocationFields;
